import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import languages from "languages";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CurrencySelector from "components/CurrencySelector";
import { capitalizeFirstLetter } from "components/usefulFunctions";

const RatesSection = ({ formData, handleChange, errors, ratesRef }) => {
  const [newRate, setNewRate] = useState({
    category: null, // keep backend key
    dailyCapacity: "", // NEW
    cost: null,
    unit: null,
    sourceLanguage: null,
    targetLanguage: null,
  });

  // Create an array of language options using the "languages" package
  const langscodes = languages.getAllLanguageCode();
  const languageOptions = langscodes.map((langcode) => {
    const languageInfo = languages.getLanguageInfo(langcode);
    return languageInfo.name;
  });

  const services = [
    "Access services",
    "Audio Description",
    "Checking/Editing",
    "Copywriting",
    "Dubbing",
    "Editing/Revision",
    "Formatting (Desktop Publishing)",
    "Interpreting",
    "Interpreting (Ad-hoc)",
    "Interpreting (Court)",
    "Interpreting (Consecutive)",
    "Interpreting (Simultaneous)",
    "Interpreting (Remote Telecom and Video)",
    "Interpreting (Whispering)",
    "Language Coaching",
    "Language Instruction",
    "Localisation",
    "Localisation (Audio Visual)",
    "Localisation (Software)",
    "Localisation (Graphics)",
    "Localisation (Website)",
    "Machine Translation Post-editing",
    "Post-editing",
    "Project Management",
    "Proofreading",
    "Sight Translation",
    "Subtitling",
    "Subtitles for the Deaf and Hard of Hearing (SDH)",
    "Summarisation",
    "Terminology Management",
    "Transcreation",
    "Transcription",
    "Translation",
    "Voiceover",
    "Other (specify in Notes)",
  ];

  const units = [
    "per source word",
    "per target word",
    "per character",
    "minimum charge",
    "per minute",
    "per page",
    "per project",
    "per document",
    "per line",
    "per hour",
    "half day",
    "full day",
    "minimum booking hours",
    "per hour overtime",
  ];

  const handleAddRate = () => {
    if (
      newRate.sourceLanguage &&
      newRate.targetLanguage &&
      newRate.category &&
      newRate.cost &&
      newRate.unit
      // dailyCapacity optional by design
    ) {
      const updatedRates = [...(formData.rates || []), newRate];
      handleChange(updatedRates, "rates");
      setNewRate({
        category: null,
        dailyCapacity: "", // reset
        cost: null,
        unit: null,
        sourceLanguage: null,
        targetLanguage: null,
      });
    }
  };

  const handleRemoveRate = (index) => {
    const newRates = [...(formData.rates || [])];
    newRates.splice(index, 1);
    handleChange(newRates, "rates");
  };

  const handleEditRate = (index) => {
    const editedRates = [...(formData.rates || [])];
    const editedRate = { ...editedRates[index] };
    editedRate.isEditing = true;
    // ensure the field exists for legacy rows
    if (editedRate.dailyCapacity === undefined) editedRate.dailyCapacity = "";
    editedRates[index] = editedRate;
    handleChange(editedRates, "rates");
  };

  const handleSaveEditedRate = (index) => {
    const editedRates = [...(formData.rates || [])];
    delete editedRates[index].isEditing;
    handleChange(editedRates, "rates");
  };

  return (
    <>
      <Grid ref={ratesRef} item xs={12} sm={12} sx={{ mt: "3rem" }}>
        <Divider sx={{ my: 2, mb: "3rem" }} />
        <Typography variant="h6" gutterBottom>
          Rates
        </Typography>
        <Typography variant="body2" gutterBottom>
          Preferred currency
        </Typography>
        <Box sx={{ my: "1rem" }}>
          <CurrencySelector
            selectedCurrency={formData.preferredCurrency}
            onSelectCurrency={(currency) =>
              handleChange(currency, "preferredCurrency")
            }
          />
        </Box>
        <Typography variant="body2" gutterBottom>
          Please enter your rates for each language pair and service offered.
          Please also enter your daily capacity in number of words, hours or
          minutes).
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12}>
        <List>
          {formData.rates.length > 1 &&
            formData.rates?.slice(0, -1).map((rate, index) => (
              <div key={`rate-${index}`}>
                {rate.isEditing ? (
                  <ListItem sx={{ px: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "calc(100% - 48px)",
                          mr: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                          }}
                        >
                          {/* Autocomplete for source languages */}
                          <Autocomplete
                            sx={{ width: "40%" }}
                            options={languageOptions}
                            size="large"
                            value={rate.sourceLanguage || ""}
                            onChange={(e, newValue) => {
                              const updatedRate = {
                                ...rate,
                                sourceLanguage: newValue,
                              };
                              const updatedRates = [...formData.rates];
                              updatedRates[index] = updatedRate;
                              handleChange(updatedRates, "rates");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select source languages"
                                error={errors.rates}
                              />
                            )}
                          />
                          {/* Autocomplete for target language */}
                          <Autocomplete
                            options={languageOptions}
                            size="large"
                            sx={{ width: "40%" }}
                            value={rate.targetLanguage || null}
                            onChange={(e, newValue) => {
                              const updatedRate = {
                                ...rate,
                                targetLanguage: newValue,
                              };
                              const updatedRates = [...formData.rates];
                              updatedRates[index] = updatedRate;
                              handleChange(updatedRates, "rates");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select target language"
                                error={errors.rates}
                              />
                            )}
                          />
                        </Box>

                        {/* Service, Daily capacity, Cost, Unit */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                            mt: 1,
                            gap: 2,
                          }}
                        >
                          {/* Service (formerly Category) */}
                          <ListItemText sx={{ width: "30%" }}>
                            <FormControl sx={{ width: "80%" }}>
                              <InputLabel
                                htmlFor={`rates-service-${index}`}
                                error={errors.rates}
                              >
                                Service
                              </InputLabel>
                              <Select
                                label="Service"
                                value={rate.category || ""}
                                error={errors.rates}
                                inputProps={{
                                  name: `ratesService-${index}`,
                                  id: `rates-service-${index}`,
                                }}
                                onChange={(e) => {
                                  const updatedRate = {
                                    ...rate,
                                    category: e.target.value,
                                  };
                                  const updatedRates = [...formData.rates];
                                  updatedRates[index] = updatedRate;
                                  handleChange(updatedRates, "rates");
                                }}
                              >
                                {services.map((service) => (
                                  <MenuItem
                                    key={service.toLowerCase()}
                                    value={service.toLowerCase()}
                                  >
                                    {service}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </ListItemText>

                          {/* Daily capacity (NEW) */}
                          <ListItemText sx={{ width: "25%" }}>
                            <TextField
                              fullWidth
                              label="Daily capacity"
                              placeholder="e.g. 2500 words"
                              value={rate.dailyCapacity ?? ""}
                              error={!!errors.rates && !rate.dailyCapacity}
                              onChange={(e) => {
                                const updatedRate = {
                                  ...rate,
                                  dailyCapacity: e.target.value,
                                };
                                const updatedRates = [...formData.rates];
                                updatedRates[index] = updatedRate;
                                handleChange(updatedRates, "rates");
                              }}
                              helperText="words / hours / minutes"
                            />
                          </ListItemText>

                          {/* Cost */}
                          <ListItemText sx={{ width: "12%" }}>
                            <TextField
                              fullWidth
                              label="Cost"
                              value={rate.cost || ""}
                              error={errors.rates}
                              onChange={(e) => {
                                const updatedRate = {
                                  ...rate,
                                  cost: e.target.value,
                                };
                                const updatedRates = [...formData.rates];
                                updatedRates[index] = updatedRate;
                                handleChange(updatedRates, "rates");
                              }}
                            />
                          </ListItemText>

                          {/* Unit */}
                          <ListItemText sx={{ width: "23%" }}>
                            <FormControl sx={{ width: "100%" }}>
                              <InputLabel
                                htmlFor={`rates-unit-${index}`}
                                error={errors.rates}
                              >
                                Unit
                              </InputLabel>
                              <Select
                                label="Unit"
                                error={errors.rates}
                                value={rate.unit || ""}
                                inputProps={{
                                  name: `ratesUnit-${index}`,
                                  id: `rates-unit-${index}`,
                                }}
                                onChange={(e) => {
                                  const updatedRate = {
                                    ...rate,
                                    unit: e.target.value,
                                  };
                                  const updatedRates = [...formData.rates];
                                  updatedRates[index] = updatedRate;
                                  handleChange(updatedRates, "rates");
                                }}
                              >
                                {units.map((unit) => (
                                  <MenuItem key={unit} value={unit}>
                                    {unit}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </ListItemText>
                        </Box>
                      </Box>

                      {/* Save Button */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          alignSelf: "stretch",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          color="secondary"
                          aria-label="save"
                          onClick={() => handleSaveEditedRate(index)}
                        >
                          <SaveIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText sx={{ width: "40%", mr: "2rem" }}>
                      {rate.sourceLanguage} into {rate.targetLanguage}
                    </ListItemText>
                    <ListItemText sx={{ width: "20%" }}>
                      {rate.category
                        ? `Service: ${capitalizeFirstLetter(rate.category)}`
                        : ""}
                    </ListItemText>
                    <ListItemText sx={{ width: "20%" }}>
                      {rate.dailyCapacity
                        ? `Daily capacity: ${rate.dailyCapacity}`
                        : ""}
                    </ListItemText>
                    <ListItemText sx={{ width: "10%" }}>
                      {rate.cost}
                    </ListItemText>
                    <ListItemText sx={{ width: "10%" }}>
                      {rate.unit}
                    </ListItemText>
                    <IconButton
                      color="secondary"
                      aria-label="edit"
                      onClick={() => handleEditRate(index)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      aria-label="delete"
                      onClick={() => handleRemoveRate(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                )}
                <Divider sx={{ my: 1 }} />
              </div>
            ))}

          {/* Add new row */}
          <div>
            <ListItem sx={{ px: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  {/* Languages */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                    }}
                  >
                    <Autocomplete
                      sx={{ width: "50%", mr: "2rem" }}
                      options={languageOptions}
                      size="large"
                      value={newRate.sourceLanguage || ""}
                      onChange={(e, newValue) => {
                        setNewRate({ ...newRate, sourceLanguage: newValue });
                        handleChange(
                          [
                            ...formData.rates.slice(0, -1),
                            {
                              sourceLanguage: newValue,
                              targetLanguage: newRate.targetLanguage,
                              category: newRate.category,
                              dailyCapacity: newRate.dailyCapacity, // NEW
                              cost: newRate.cost,
                              unit: newRate.unit,
                            },
                          ],
                          "rates"
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select source language"
                          error={errors.rates}
                        />
                      )}
                    />
                    <Autocomplete
                      options={languageOptions}
                      size="large"
                      sx={{ width: "50%" }}
                      value={newRate.targetLanguage || null}
                      onChange={(e, newValue) => {
                        setNewRate({ ...newRate, targetLanguage: newValue });
                        handleChange(
                          [
                            ...formData.rates.slice(0, -1),
                            {
                              sourceLanguage: newRate.sourceLanguage,
                              targetLanguage: newValue,
                              category: newRate.category,
                              dailyCapacity: newRate.dailyCapacity, // NEW
                              cost: newRate.cost,
                              unit: newRate.unit,
                            },
                          ],
                          "rates"
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select target language"
                          error={errors.rates}
                        />
                      )}
                    />
                  </Box>

                  {/* Service, Daily capacity, Cost, Unit */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      mt: 1,
                    }}
                  >
                    {/* Service (formerly Category) */}
                    <ListItemText sx={{ width: "30%" }}>
                      <FormControl sx={{ width: "90%" }}>
                        <InputLabel
                          htmlFor="rates-service"
                          error={errors.rates}
                        >
                          Service
                        </InputLabel>
                        <Select
                          label="Service"
                          value={newRate.category || ""}
                          error={errors.rates}
                          inputProps={{
                            name: "ratesService",
                            id: "rates-service",
                          }}
                          onChange={(e) => {
                            const category = e.target.value;
                            setNewRate({ ...newRate, category });
                            handleChange(
                              [
                                ...formData.rates.slice(0, -1),
                                {
                                  sourceLanguage: newRate.sourceLanguage,
                                  targetLanguage: newRate.targetLanguage,
                                  category,
                                  dailyCapacity: newRate.dailyCapacity, // NEW
                                  cost: newRate.cost,
                                  unit: newRate.unit,
                                },
                              ],
                              "rates"
                            );
                          }}
                        >
                          {services.map((service) => (
                            <MenuItem
                              key={service.toLowerCase()}
                              value={service.toLowerCase()}
                            >
                              {service}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItemText>

                    {/* Daily capacity (NEW) */}
                    <ListItemText sx={{ width: "25%", mr: "1rem" }}>
                      <TextField
                        fullWidth
                        error={errors.rates && !newRate.dailyCapacity}
                        label="Daily capacity"
                        placeholder="e.g. 2500 words"
                        value={newRate.dailyCapacity}
                        onChange={(e) => {
                          const dailyCapacity = e.target.value;
                          setNewRate({ ...newRate, dailyCapacity });
                          handleChange(
                            [
                              ...formData.rates.slice(0, -1),
                              {
                                sourceLanguage: newRate.sourceLanguage,
                                targetLanguage: newRate.targetLanguage,
                                category: newRate.category,
                                dailyCapacity, // NEW
                                cost: newRate.cost,
                                unit: newRate.unit,
                              },
                            ],
                            "rates"
                          );
                        }}
                      />
                    </ListItemText>

                    {/* Cost */}
                    <ListItemText sx={{ width: "12%" }}>
                      <TextField
                        fullWidth
                        error={errors.rates}
                        label="Cost"
                        value={newRate.cost || ""}
                        onChange={(e) => {
                          const cost = e.target.value;
                          setNewRate({ ...newRate, cost });
                          handleChange(
                            [
                              ...formData.rates.slice(0, -1),
                              {
                                sourceLanguage: newRate.sourceLanguage,
                                targetLanguage: newRate.targetLanguage,
                                category: newRate.category,
                                dailyCapacity: newRate.dailyCapacity, // NEW
                                cost,
                                unit: newRate.unit,
                              },
                            ],
                            "rates"
                          );
                        }}
                      />
                    </ListItemText>

                    {/* Unit */}
                    <ListItemText sx={{ width: "23%", ml: "1rem" }}>
                      <FormControl sx={{ width: "100%" }}>
                        <InputLabel htmlFor="rates-unit" error={errors.rates}>
                          Unit
                        </InputLabel>
                        <Select
                          label="Unit"
                          value={newRate.unit || ""}
                          error={errors.rates}
                          inputProps={{
                            name: "ratesUnit",
                            id: "rates-unit",
                          }}
                          onChange={(e) => {
                            const unit = e.target.value;
                            setNewRate({ ...newRate, unit });
                            handleChange(
                              [
                                ...formData.rates.slice(0, -1),
                                {
                                  sourceLanguage: newRate.sourceLanguage,
                                  targetLanguage: newRate.targetLanguage,
                                  category: newRate.category,
                                  dailyCapacity: newRate.dailyCapacity, // NEW
                                  cost: newRate.cost,
                                  unit,
                                },
                              ],
                              "rates"
                            );
                          }}
                        >
                          {units.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItemText>
                  </Box>
                </Box>
              </Box>
            </ListItem>

            {/* Add button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: "1rem" }}>
              <IconButton
                color="secondary"
                aria-label="add"
                onClick={handleAddRate}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </div>
        </List>
      </Grid>
    </>
  );
};

export default RatesSection;
